import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Modal, Table, Link, Space, Tooltip } from '@bedrock/components';
import { Link as LinkIcon } from '@bedrock/icons-react';
import { useMemoizedFn, useRequest } from 'ahooks';
import classNames from 'classnames/bind';
import styles from './index.less';
import CooperativeContents, { useOpenContentDetails } from '../CooperativeContents';
import CopyIcon from '@mpc/common/src/components/CopyIcon';
import { $sType } from '@mpc/common/src/utils';
import { contentMarketQueryGetMonitorLinks, outsourceContentMarketQueryGetMonitorLinks, MonitorLinkItemVo } from '@mpc/common/src/services/api';

const cx = classNames.bind(styles);

export interface StarChartMonitoringLinkModalProps {
  visible: boolean;
  onClose: () => void;
  detail?: any;
}

/** 表格数据类型 */
interface TableDataItem {
  id: number;
  cooperationContentId: string;
  cooperationContentName: string;
  link: string;
  property_values: any[];
  necessary_rights: any[];
  expect_rights_diff_list: any[];
  kpi_values: any[];
  cooperate_account_id: any;
  remark: string;
  rowSpan?: number;
}

/**
 * 星图监测链接弹窗组件
 * 展示监测链接表格，支持查看合作内容详情和复制链接
 */
export default function StarChartMonitoringLinkModal(props: StarChartMonitoringLinkModalProps) {
  const { visible, onClose, detail } = props;
  const { openContentDetails } = useOpenContentDetails({ detail });

  // 获取监测链接数据
  const { data: monitorLinksData, loading } = useRequest(
    async () => {
      if (!visible || !detail?.id) return [];
      const api = $sType === 'inner' ? contentMarketQueryGetMonitorLinks : outsourceContentMarketQueryGetMonitorLinks;
      const res = await api({
        case_demand_id: detail.id,
      });
      return res?.data || [];
    },
    {
      refreshDeps: [visible, detail?.id],
    }
  );

  // 将监测链接数据与合作内容匹配，生成表格数据
  const tableData = useMemo<TableDataItem[]>(() => {
    if (!monitorLinksData?.length || !detail?.demand_plan_info?.cooperate_accounts) {
      return [];
    }

    const cooperateAccounts = detail.demand_plan_info.cooperate_accounts;
    const result: any[] = [];

    // 按合作内容分组
    const groupedByContent = new Map<string, MonitorLinkItemVo[]>();
    monitorLinksData.forEach((link) => {
      const key = link.sub_demand_index_id || 'unknown';
      if (!groupedByContent.has(key)) {
        groupedByContent.set(key, []);
      }
      groupedByContent.get(key)!.push(link);
    });

    // 生成表格数据，支持单元格合并
    groupedByContent.forEach((links, subDemandIndexId) => {
      const cooperateAccount = cooperateAccounts.find(
        (account: any) => +account.index_id === +subDemandIndexId
      );

      links.forEach((link, index) => {
        result.push({
          id: link.link_index || 0,
          index_id: link.sub_demand_index_id || 0,
          cooperationContentId: subDemandIndexId,
          cooperationContentName: cooperateAccount?.name || link.kol_name || '未知合作内容',
          link: link.track_url || '',
          property_values: cooperateAccount?.property_values || [],
          necessary_rights: cooperateAccount?.necessary_rights || [],
          expect_rights_diff_list: cooperateAccount?.expect_rights_diff_list || [],
          kpi_values: cooperateAccount?.kpi_values || [],
          cooperate_account_id: cooperateAccount?.id,
          remark: cooperateAccount?.remark || '',
          rowSpan: index === 0 ? links.length : 0,
        });
      });
    });

    return result;
  }, [monitorLinksData, detail]);

  // 打开合作内容详情
  const handleOpenContentDetails = useMemoizedFn((record: TableDataItem) => {
    openContentDetails({
      title: record.cooperationContentName,
      property_values: record.property_values,
      necessary_rights: record.necessary_rights,
      expect_rights_diff_list: record.expect_rights_diff_list,
      kpi_values: record.kpi_values,
      cooperate_account_id: record.cooperate_account_id,
      remark: record.remark,
    });
  });

  // 表格列配置
  const columns = useMemo(() => [
    {
      title: '合作内容',
      align: 'left' as const,
      dataIndex: 'cooperationContentName',
      key: 'cooperationContentName',
      width: 320,
      className: "align-top",
      render: (text: string, record: TableDataItem) => {
        // 如果 rowSpan 为 0，表示被合并，不渲染内容
        if (record.rowSpan === 0) {
          return {
            children: null,
            props: {
              rowSpan: 0,
            },
          };
        }
        return {
          children: (
            <CooperativeContents
              Cooperative={{
                id: record.cooperate_account_id,
                property_values: record.property_values,
                necessary_rights: record.necessary_rights,
                expect_rights_diff_list: record.expect_rights_diff_list,
                kpi_values: record.kpi_values,
                remark: record.remark,
              }}
              index_id={record?.index_id || 0}
              detail={detail}
              showDetailBtn={true}
              sliceEnd={4}
            />
          ),
          props: {
            rowSpan: record.rowSpan || 1,
          },
        };
      },
    },
    {
      title: '链接编号',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center' as const,
    },
    {
      title: '星图度量监测链接',
      align: 'left' as const,
      dataIndex: 'link',
      key: 'link',
      render: (link: string) => (
        <div className={cx('link-cell')}>
          <Tooltip title={link}>
            <Link href={link} target="_blank" className={cx('monitoring-link')}>
              <LinkIcon className={cx('mr-1')}/>{link}
            </Link>
          </Tooltip>
          <CopyIcon text={link} tipToolTitle="复制链接"  className="items-center" />
        </div>
      ),
    },
  ], [handleOpenContentDetails]);
  const modalRef = useRef<HTMLDivElement>(null);

  return (
    <Modal
      className={cx('monitoring-link-modal', 'monitoring-link-modal-wrapper')}
      title="星图监测链接"
      visible={visible}
      onCancel={onClose}
      width={1000}
      footer={null}
      wrapProps={{
        ref: modalRef,
      }}
    >
      <Table
        sticky={{
          getContainer: () => {
            console.log(111, modalRef.current?.querySelector?.('.rock-scrollbar-view'));
            // return modalRef?.current?.querySelector?.('.rock-modal-body') as HTMLElement;
            return document.querySelector?.('.rock-modal-body') as HTMLElement;
          },
        }}
        className={cx('monitoring-link-table')}
        columns={columns}
        data={tableData}
        rowKey="id"
        pagination={false}
        verticalBorder={true}
      />
    </Modal>
  );
}
